import { Injectable } from '@angular/core';
import { DockBrief } from '../../../../administration/dock/models';
import { LocalDateModel } from '../../../../../models';
import { CompartmentRuleItem } from '../../../../administration/compartment/models';
import { AllocationViewAllocateActionState } from './allocation-view-allocate-action.state';
import { AllocationViewActionsFacade } from '../allocation-view-actions.facade';
import {
  AllocateActionRequestModel,
  AllocationByRuleActionRequestModel,
  AllocationDetailsModel,
  AllocationManualActionRequestModel,
  AllocationResponseModel,
  AllocationUliDetails,
  AllocationUliRuleSlot,
  AllocationViewSelectedEntityModel,
  AllocationViewTimeslotInputModel,
} from '../../models';
import { AllocationViewActionInterface, AllocationViewCombinedActionInterface } from '../models';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { delay, filter, take } from 'rxjs/operators';
import {
  AllocationViewAllocateActionAllocateByRuleUli,
  AllocationViewAllocateActionAutoAllocateUli,
  AllocationViewAllocateActionLoadAllocationDetails,
  AllocationViewAllocateActionLoadCompartments,
  AllocationViewAllocateActionLoadDocks,
  AllocationViewAllocateActionLoadProposedTimeslot,
  AllocationViewAllocateActionLoadRuleSlots,
  AllocationViewAllocateActionLoadRuleSlotsWithoutDetails,
  AllocationViewAllocateActionLoadRules,
  AllocationViewAllocateActionManualAllocateUli,
  AllocationViewAllocateActionResetProposedTimeslot,
  AllocationViewAllocateActionSetCloseAfterCompleteState,
  AllocationViewAllocateActionSetInitialState,
  AllocationViewAllocateActionUpdateAllocationDetails,
} from './allocation-view-allocate-action.actions';
import { ActiveCompartmentsModel } from '../../uli';

@Injectable({ providedIn: 'root' })
export class AllocationViewAllocateActionFacade
  implements AllocationViewActionInterface, AllocationViewCombinedActionInterface
{
  allocationDockOptions$: Observable<DockBrief[]> = this.store.select(
    AllocationViewAllocateActionState.getDocksForAllocation,
  );
  allocationRuleOptions$: Observable<CompartmentRuleItem[]> = this.store.select(
    AllocationViewAllocateActionState.getRulesForAllocation,
  );
  allocationRuleSlotOptions$: Observable<AllocationUliRuleSlot[]> = this.store.select(
    AllocationViewAllocateActionState.getRuleSlotsForAllocation,
  );
  proposedTimeslot$: Observable<AllocationViewTimeslotInputModel> = this.store.select(
    AllocationViewAllocateActionState.getProposedTimeslot,
  );
  allocationDetails$: Observable<AllocationDetailsModel> = this.store.select(
    AllocationViewAllocateActionState.getAllocationDetails,
  );
  compartments$: Observable<ActiveCompartmentsModel[]> = this.store.select(
    AllocationViewAllocateActionState.getCompartments,
  );
  responseData$: Observable<AllocationResponseModel> = this.store.select(
    AllocationViewAllocateActionState.getResponseData,
  );
  loaded$: Observable<boolean> = this.store.select(AllocationViewAllocateActionState.getLoaded);

  constructor(protected store: Store, protected allocationViewActionsFacade: AllocationViewActionsFacade) {}

  loadAll(): void {
    this.allocationViewActionsFacade.actionIdentifier$
      .pipe(delay(0), filter(Boolean), take(1))
      .subscribe((actionIdentifier: AllocationViewSelectedEntityModel) => {
        this.store.dispatch(new AllocationViewAllocateActionLoadAllocationDetails(actionIdentifier));
      });
  }

  updateAllocationDetails(details: AllocationDetailsModel): void {
    const uliDetails = details.uliDetails.map((item: AllocationUliDetails) => ({
      ...item,
      scheduledArrivalTime: LocalDateModel.getUtcDate(
        item.scheduledArrivalTime.timestamp,
        item.scheduledArrivalTime.timezone,
      ),
      scheduledDepartureTime: LocalDateModel.getUtcDate(
        item.scheduledDepartureTime.timestamp,
        item.scheduledDepartureTime.timezone,
      ),
    }));
    this.store.dispatch(new AllocationViewAllocateActionUpdateAllocationDetails({ ...details, uliDetails }));
  }

  loadCompartmentList(hubId: number): void {
    this.store.dispatch(new AllocationViewAllocateActionLoadCompartments(hubId));
  }

  loadAllocationDockList(details: AllocateActionRequestModel): void {
    this.store.dispatch(new AllocationViewAllocateActionLoadDocks(details));
  }

  loadAllocationRuleList(payload: AllocateActionRequestModel): void {
    const { details } = payload;
    const uliDetails: AllocationUliDetails[] = details.uliDetails.map((uli: AllocationUliDetails) => {
      const { scheduledArrivalTime, scheduledDepartureTime } = uli;
      return {
        ...uli,
        scheduledArrivalTime: LocalDateModel.getUtcDate(scheduledArrivalTime.timestamp, scheduledArrivalTime.timezone),
        scheduledDepartureTime: LocalDateModel.getUtcDate(
          scheduledDepartureTime.timestamp,
          scheduledDepartureTime.timezone,
        ),
      };
    });
    this.store.dispatch(new AllocationViewAllocateActionLoadRules({ ...payload, details: { ...details, uliDetails } }));
  }

  loadAllocationRuleSlotList(details: AllocationDetailsModel, ruleId: number): void {
    const uliDetails: AllocationUliDetails[] = details.uliDetails.map((uli: AllocationUliDetails) => {
      const { scheduledArrivalTime, scheduledDepartureTime } = uli;
      return {
        ...uli,
        scheduledArrivalTime: LocalDateModel.getUtcDate(scheduledArrivalTime.timestamp, scheduledArrivalTime.timezone),
        scheduledDepartureTime: LocalDateModel.getUtcDate(
          scheduledDepartureTime.timestamp,
          scheduledDepartureTime.timezone,
        ),
      };
    });
    this.store.dispatch(new AllocationViewAllocateActionLoadRuleSlots({ details: { ...details, uliDetails }, ruleId }));
  }

  loadAllocationRuleSlotListWithoutDetails(hubId: number, ruleId: number): void {
    this.store.dispatch(new AllocationViewAllocateActionLoadRuleSlotsWithoutDetails({ ruleId, hubId }));
  }

  loadProposedTimeslot(compartmentId: number, dockId: number, details: AllocationDetailsModel): void {
    this.store.dispatch(
      new AllocationViewAllocateActionLoadProposedTimeslot({
        compartmentId,
        dockId,
        details,
      }),
    );
  }

  resetProposedTimeslot(): void {
    this.store.dispatch(new AllocationViewAllocateActionResetProposedTimeslot());
  }

  allocateUli(data: AllocationManualActionRequestModel): void {
    this.allocationViewActionsFacade.actionIdentifier$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.store.dispatch(new AllocationViewAllocateActionManualAllocateUli(data));
    });
  }

  autoAllocateUli(details: AllocateActionRequestModel): void {
    this.allocationViewActionsFacade.actionIdentifier$.pipe(delay(0), take(1), filter(Boolean)).subscribe(() => {
      this.store.dispatch(new AllocationViewAllocateActionAutoAllocateUli(details));
    });
  }

  allocateByRuleUli(data: AllocationByRuleActionRequestModel): void {
    this.store.dispatch(new AllocationViewAllocateActionAllocateByRuleUli(data));
  }

  setCloseContainerAfterCompleteState(closeAfterComplete: boolean): void {
    this.store.dispatch(new AllocationViewAllocateActionSetCloseAfterCompleteState(closeAfterComplete));
  }

  setInitialState(): void {
    this.store.dispatch(new AllocationViewAllocateActionSetInitialState());
  }
}
